import axios, { AxiosResponse } from 'axios';
import { FormattedMessage, useIntl } from 'gatsby-plugin-intl';
import * as Cookie from 'js-cookie';
import * as React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { animated, useSpring } from 'react-spring';
import styled, { css } from 'styled-components';

import { CONFIG } from '../../config';
import { EApiStatuses, useApiStatus } from '../../hooks/useApiStatus';
import { useCurrentLang } from '../../hooks/useCurrentLang';
import { useGlobalActions, useGlobalStore } from '../../hooks/useGlobal';
import { useTheme } from '../../hooks/useTheme';
import { media } from '../../styles/theme';
import { parseError } from '../../styles/utils';
import { Alert } from '../common/Alert';
import { ButtonPrimary, ButtonSecondary } from '../common/Buttons';
import { Checkbox } from '../common/Checkbox';
import { Flex } from '../common/Flex';
import { Icon } from '../common/Icon';
import { Indent } from '../common/Indent';
import { Input } from '../common/Input';
import { StyledAnchor } from '../common/StyledLink';
import { StyledText } from '../common/StyledText';
import { SelectInput, ListItem, Select, ISelectOptionItem } from '../common/Select';

interface IProps {
  onUpdateError?: () => void;
  large?: boolean;
}

interface ICountry {
  code: string;
  countryName: string;
  countryNameEn: string;
  country: string;
}

interface IOptionCountry extends ISelectOptionItem {
  code: string;
  countryName: string;
  country: string;
}

export const SignUpForm: React.FC<IProps> = props => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const currentLang = useCurrentLang();
  const [error, setError] = React.useState('');
  const [inviteCode, setInviteCode] = React.useState('');
  const [phoneCode, setPhoneCode] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [confirmCode, setConfirmCode] = React.useState('');
  const [codeSended, setCodeSended] = React.useState(false);
  const { email, password } = useGlobalStore();
  const [status, setStatus] = useApiStatus();
  const [statusConfirmPhone, setStatusConfirmPhone] = useApiStatus();
  const { setPassword, setEmail } = useGlobalActions();
  const [terms, setTerms] = React.useState(false);
  const theme = useTheme();
  const [timeoutResend, setTimeoutResend] = React.useState(Date.now());
  const [now, setNow] = React.useState(Date.now());
  const intl = useIntl();
  const [step, setStep] = React.useState(1);
  const [countries, setCountries] = React.useState<ICountry[]>([]);
  const firstRender = React.useRef(true);
  React.useEffect(() => {
    if (props.onUpdateError) props.onUpdateError();
  }, [error, status]);

  React.useEffect(() => {
    const timeout = setTimeout(fetchCountries(), firstRender.current ? 1000 : 0);
    firstRender.current = false;
    return () => {
      clearTimeout(timeout);
    };
  }, [currentLang]);

  React.useEffect(() => {
    setError('');
  }, [inviteCode, email, password, phoneCode, phone, confirmCode]);

  const resendSeconds = Math.ceil((timeoutResend - now) / 1000);

  React.useEffect(() => {
    if (resendSeconds > 0) {
      setTimeout(() => {
        setNow(Date.now());
      }, 1000);
    }
  }, [resendSeconds]);

  const steps = useSpring({
    transform: `translateX(-${step < 2 ? 0 : 50}%)`,
  });

  const stepsIndicators = useSpring({
    opacity: step > 1 ? 1 : 0,
    flex: '1 1 auto',
  });

  const codeFields = useSpring({
    maxHeight: codeSended ? 180 : 0,
    overflow: 'hidden',
  });

  const countriesList = React.useMemo(() => {
    return countries.map(({ country, countryName, code }) => ({
      label: (
        <Indent vertical={0.75} width='100%'>
          <Flex align='center'>
            <Indent right={1}>
              <Flag src={`https://static.expertoption.com/flags/svg/4x3/${country}.svg`} alt={countryName} />
            </Indent>
            <ItemEllipsis>{countryName}</ItemEllipsis>
            <Indent left={1}>
              <StyledText size='inherit' color='textShadowed'>
                {code}
              </StyledText>
            </Indent>
          </Flex>
        </Indent>
      ),
      value: country,
      country,
      code,
      countryName,
    }));
  }, [countries]);

  return (
    <form onSubmit={handleSubmit}>
      <Indent type='block' bottom={0.3}>
        <StyledText weight='700' size={2} color='white' lineHeight='40px' onClick={() => setStep(2)}>
          <FormattedMessage id='auth.signUpTitle' />
        </StyledText>
      </Indent>
      <div style={{ height: 50 }}>
        <Flex align='center'>
          <Indent right={1}>
            <StyledText color='#858A8F' size={0.9}>
              {step !== 1 ? (
                <FormattedMessage id='auth.signUpStep' values={{ current: step, of: '3' }} />
              ) : (
                <FormattedMessage id='auth.signUpSubTitle' />
              )}
            </StyledText>
          </Indent>
          <animated.div style={stepsIndicators}>
            <Flex align='center'>
              <StepIndicator active={step >= 2} />
              <StepIndicator active={step >= 3} />
              <StepIndicator active={step >= 4} />
            </Flex>
          </animated.div>
        </Flex>
      </div>
      <Alert show={!!error}>{error}</Alert>
      <Steps>
        <StepBox style={steps}>
          <Step active={step === 1}>
            <Indent type='block' bottom={1}>
              <Input
                value={email}
                required
                large
                autoComplete='email'
                onChange={e => setEmail(e.target.value)}
                placeholder={intl.formatMessage({ id: 'fields.email' })}
              />
            </Indent>
            <Indent type='block' bottom={1}>
              <Input
                value={password}
                required
                large
                onChange={e => setPassword(e.target.value)}
                type='password'
                autoComplete='new_password'
                placeholder={intl.formatMessage({ id: 'fields.password' })}
              />
            </Indent>
            <Indent type='block' bottom={1}>
              <Input
                value={inviteCode}
                large
                onChange={e => setInviteCode(e.target.value)}
                placeholder={intl.formatMessage({ id: 'fields.invite_code' })}
              />
            </Indent>
            <Indent type='block' bottom={2}>
              <Flex justify='space-between' align='center'>
                <Checkbox
                  defaultCheked={terms}
                  onChange={value => {
                    setError('');
                    setTerms(value);
                  }}>
                  <FormattedMessage id='common.termsAndConditions' />
                </Checkbox>
                <StyledAnchor href={`/${intl.locale}/terms`} target='_blank'>
                  <Icon name='information' color={theme.primaryColor} width='20px' />
                </StyledAnchor>
              </Flex>
            </Indent>

            <ButtonPrimary
              size='medium'
              isLoading={status === EApiStatuses.Processing}
              type='button'
              onClick={handleSubmit}>
              <FormattedMessage id='common.signinBtn' />
            </ButtonPrimary>
          </Step>

          <Step active={step >= 2}>
            <Indent type='block' bottom={1}>
              <Flex gap='1em'>
                {countriesList.length ? (
                  <Select<IOptionCountry>
                    style={{
                      flex: '1 1 30%',
                    }}
                    columns={1}
                    width='350px'
                    maxItemsInColumn={5}
                    onChange={code => {
                      setPhoneCode(code);
                    }}
                    isInput
                    defaultValue={phoneCode}
                    label={({ country, countryName, code }) => (
                      <Flex align='center'>
                        <Flag src={`https://static.expertoption.com/flags/svg/4x3/${country}.svg`} alt={countryName} />
                        {code}
                      </Flex>
                    )}
                    options={countriesList}
                  />
                ) : (
                  <Input
                    style={{
                      flex: '1 1 40%',
                    }}
                    value={phoneCode}
                    large
                    onChange={e => setPhoneCode(e.target.value)}
                    placeholder={intl.formatMessage({ id: 'fields.phone_code' })}
                  />
                )}

                <Input
                  style={{
                    flex: '1 1 50%',
                  }}
                  value={phone}
                  large
                  onChange={e => setPhone(e.target.value)}
                  placeholder={intl.formatMessage({ id: 'fields.phone' })}
                />
              </Flex>
            </Indent>

            <animated.div style={codeFields}>
              <Indent type='block' bottom={1.5}>
                <ButtonSecondary
                  isLoading={statusConfirmPhone === EApiStatuses.Processing}
                  onClick={sendConfirmCode}
                  disabled={!phone || !phoneCode || (codeSended && resendSeconds > 0)}>
                  <FormattedMessage id='register.resend_confirm_code' />
                </ButtonSecondary>
                <Indent type='block' bottom={0.25} top={0.25}>
                  <div style={{ height: 15 }}>
                    {resendSeconds > 0 && (
                      <StyledText color='textShadowed' size='0.8'>
                        <FormattedMessage
                          id='register.resend_timer'
                          values={{
                            timer: resendSeconds,
                          }}
                        />
                      </StyledText>
                    )}
                  </div>
                </Indent>
              </Indent>

              <Indent type='block' bottom={1}>
                <Input
                  value={confirmCode}
                  large
                  onChange={e => setConfirmCode(e.target.value)}
                  placeholder={intl.formatMessage({ id: 'fields.confirm_code' })}
                />
              </Indent>
            </animated.div>

            <ButtonPrimary
              isLoading={
                codeSended ? status === EApiStatuses.Processing : statusConfirmPhone === EApiStatuses.Processing
              }
              onClick={codeSended ? confirmPhone : sendConfirmCode}
              disabled={!phone || !phoneCode}>
              {codeSended ? (
                <FormattedMessage id='register.confirm_phone' />
              ) : (
                <FormattedMessage id='register.get_confirm_code' />
              )}
            </ButtonPrimary>
            {/* <Flex align='center' height='48px' justify='center'>
              <StyledText size='0.8' color='textShadowed'>
                <StyledAnchor
                  href='#'
                  onClick={event => {
                    event.nativeEvent.preventDefault();
                    redirectToAffiliate();
                  }}>
                  <FormattedMessage id={'registration.skip'} />
                </StyledAnchor>
              </StyledText>
            </Flex> */}
          </Step>
        </StepBox>
      </Steps>
    </form>
  );

  async function handleSubmit(e) {
    e.preventDefault();

    if (!terms) {
      setError(intl.formatMessage({ id: 'common.termsError' }));
      return;
    }

    setStatus(EApiStatuses.Processing);
    setError('');
    const googleReCaptcha = await executeRecaptcha('registration');
    Cookie.remove('sess_token_');
    Cookie.remove('sess_token_', {
      domain: '.' + location.host,
      path: '/',
    });
    Cookie.remove('sess_token_');

    try {
      const response: AxiosResponse<{ token: string }> = await axios({
        method: 'post',
        url: `${CONFIG.API_URL}/auth/register/`,
        data: {
          email,
          password,
          password2: password,
          skype: '',
          invite_code: inviteCode,
          wm: Cookie.get('wm'),
          wmtr: Cookie.get('wmtr'),
          wmref: Cookie.get('wmref'),
          confirm_terms: terms ? '1' : '0',
          googleReCaptcha,
        },
      });
      dataLayer.push({ event: 'registration' });
      setStatus(EApiStatuses.Success);
      Cookie.set('sess_token_', response.data.token, CONFIG.COOKIE_CONFIG);
      setStep(2);
    } catch (e) {
      setError(
        intl.formatMessage({
          id: 'common.errors.' + parseError(e.response?.data?.errors),
        }),
      );
      setStatus(EApiStatuses.Failed);
    }
  }

  function redirectToAffiliate() {
    setStep(4);
    setTimeout(() => {
      window.location.pathname = '/partner/index.cgi';
    }, 500);
  }

  async function confirmPhone(e) {
    e.preventDefault();

    if (statusConfirmPhone === EApiStatuses.Processing) {
      return;
    }
    setStatus(EApiStatuses.Processing);
    setError('');

    try {
      await axios({
        method: 'post',
        url: `${CONFIG.API_URL}/profile_settings/change_phone/`,
        headers: {
          Authorization: `Bearer ${Cookie.get('sess_token_')}`,
        },
        data: { phone_code: getCodeFromCountries(), phone, confirm_code: confirmCode },
      });
      setNow(Date.now());
      setCodeSended(true);
      setStatus(EApiStatuses.Pending);
      redirectToAffiliate();
    } catch (e) {
      if (Array.isArray(e.response?.data?.errors)) {
        setError(
          intl.formatMessage({
            id: 'common.errors.' + parseError(e.response?.data?.errors),
          }),
        );
      } else {
        setError(
          intl.formatMessage({
            id: 'common.errors.' + (e.response?.data?.errors?.phone_code?.[0] || e.response?.data?.errors?.phone?.[0]),
          }),
        );
      }
      setStatus(EApiStatuses.Failed);
    }
  }

  async function sendConfirmCode(e) {
    e.preventDefault();

    if (statusConfirmPhone === EApiStatuses.Processing) {
      return;
    }
    setStatusConfirmPhone(EApiStatuses.Processing);
    setError('');

    try {
      const response = await axios({
        method: 'post',
        url: `${CONFIG.API_URL}/phone/check/`,
        data: { phone_code: getCodeFromCountries(), phone },
      });
      setTimeoutResend(Date.now() + (response.data?.timeout || 60) * 1000);
      setNow(Date.now());
      setCodeSended(true);
      setStatusConfirmPhone(EApiStatuses.Pending);
      setStep(3);
    } catch (e) {
      if (Array.isArray(e.response?.data?.errors)) {
        setError(
          intl.formatMessage({
            id: 'common.errors.' + parseError(e.response?.data?.errors),
          }),
        );
      } else {
        setError(
          intl.formatMessage({
            id: 'common.errors.' + (e.response?.data?.errors?.phone_code?.[0] || e.response?.data?.errors?.phone?.[0]),
          }),
        );
      }
      setStatusConfirmPhone(EApiStatuses.Failed);
    }
  }

  async function fetchCountries() {
    try {
      const response = await axios({
        method: 'get',
        url: CONFIG.PHONE_API_URL,
        params: {
          locale: currentLang,
        },
      });
      setPhoneCode(response.data.defaultCountry);
      setCountries(response.data.countries.sort((item, itemB) => item.countryName.localeCompare(itemB.countryName)));
    } catch (e) {}
  }

  function getCodeFromCountries() {
    return countries.find(item => item.country === phoneCode).code;
  }
};

const Steps = styled.div`
  position: relative;

  overflow-x: hidden;
  width: calc(100% + 40px);
  margin: 0 -20px;

  ${media.sm`;
	width: calc(100% + 190px);
  margin: 0 -95px;
	`};
`;

const applyFlagStyles = ({ theme }) => css`
  transition-duration: ${theme.transitionDuration};
  transition-timing-function: ${theme.transitionFunction};
`;
const Flag = styled.img`
  height: 18px;
  width: 24px;
  object-fit: cover;
  border-radius: 4px;
  box-shadow: 0 0 0 0 hsla(0, 0%, 100%, 0.2);
  transition: box-shadow;
  margin-right: 8px;
  ${applyFlagStyles};

  ${SelectInput}:hover > & {
    box-shadow: 0 0 0 3px hsla(0, 0%, 100%, 0.2);
  }

  ${ListItem} & {
    height: 1.1em;
    border-radius: 2px;
    width: 1.5em;
    margin-right: 0;
  }
`;

const StepBox = styled(animated.div)`
  display: flex;
  width: 200%;
`;

const Step = styled.div<{ active: boolean }>`
  width: 50%;
  padding: 0 20px;

  transition: opacity 400ms, transform 300ms;
  transform: scale(${({ active }) => (active ? 1 : 0.85)});
  opacity: ${({ active }) => (active ? 1 : 0.6)};
  ${media.sm`

	padding: 0 95px;
	`}
`;

const ItemEllipsis = styled.div`
  flex: 1 1 auto;
  width: calc(100% - 20px - 1rem - 30px - 1rem);
  text-overflow: ellipsis;
  word-break: inherit;
  white-space: nowrap;
  overflow: hidden;
  height: 20px;
  line-height: 20px;
`;

const StepIndicator = styled.div<{ active: boolean }>`
  flex: 1 1 auto;
  margin-right: 4px;
  height: 4px;
  border-radius: 8px;
  background: #131b2b;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: block;
    transform: translateX(${({ active }) => (active ? '0' : '-100%')});
    background: #259efa;
    transition: transform 1000ms;
  }
`;
